import { atom, atomFamily, selector, DefaultValue, selectorFamily } from 'recoil';
import { traceHPMutation } from './hpMutationTrace';
import type { RecoilState, RecoilValueReadOnly } from 'recoil';
import { nanoid } from 'nanoid';
import {
  broadcastChannelSynchronizerEffect,
  localForagePersisterEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import type { HangingProtocol } from './useHangingProtocol';
import type { StepConfiguration } from './steppedHPTypes';
import { calculateSubStepTotal } from './hpStepController';
import { markUnsavedStepEditsHydrated } from './unsavedStepEditsHydration';
import omit from 'ramda/es/omit';
import { StepBarPlacement } from 'generated/graphql';
import { FLAT_BUCKET } from '../ViewportsConfigurations/stepBuckets';
import type { StepBucketKey } from '../ViewportsConfigurations/stepBuckets';

// Defined locally to avoid depending on Steps/Common.tsx (a UI file) from this pure-state module.
// Must stay in sync with STEP_BAR_DEFAULT_HEIGHT in Steps/Common.tsx.
const STEP_BAR_DEFAULT_HEIGHT = 48;

// Re-export the generated StepBarPlacement enum so the placement atom and the Steps components
// share a single enum (both resolve to generated/graphql) rather than two nominal copies.
export { StepBarPlacement };

export const previousHangingProtocolState: RecoilState<Nullable<HangingProtocol>> = atom<
  Nullable<HangingProtocol>
>({
  key: 'previousHangingProtocolState',
  default: null,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
    }),
  ],
});

// In-memory only (no localForage persister): an unconfirmed preview must never survive a
// refresh. broadcastChannelSynchronizerEffect is kept so multiple viewer windows for the same
// case still see the same live preview. Confirming copies the value into
// selectedHangingProtocolSmidState (below), which is the atom that actually persists.
export const previewHangingProtocolState: RecoilState<{
  [key: string]: Nullable<HangingProtocol>;
}> = atom({
  key: 'previewHangingProtocolState',
  default: {},
  effects: [broadcastChannelSynchronizerEffect()],
});

export const previewHangingProtocolSelector: (
  arg1?: Nullable<string>
) => RecoilState<Nullable<HangingProtocol>> = selectorFamily<
  Nullable<HangingProtocol>,
  Nullable<string>
>({
  key: 'previewHangingProtocolSelector',
  get:
    (caseSmid?: string | null) =>
    ({ get }) => {
      const state = get(previewHangingProtocolState);
      return state != null && caseSmid != null ? state[caseSmid] : null;
    },
  set:
    (caseSmid?: string | null) =>
    ({ set, reset }, hangingProtocol: Nullable<DefaultValue | HangingProtocol>) => {
      if (caseSmid == null) return;

      if (hangingProtocol instanceof DefaultValue && caseSmid != null) {
        set(previewHangingProtocolState, (state) => omit([caseSmid], state));
        return;
      }

      // @ts-expect-error [EN-7967] - TS2345 - Argument of type '(state: { [key: string]: { readonly __typename?: "HangingProtocol"; readonly smid: string; readonly name: string; readonly numberOfPriors: number; readonly created: Date; readonly updated: Date; readonly studyDescriptions: readonly string[]; readonly studyCriteria?: JSON; readonly data: { ...; }; readonly viewportsC...' is not assignable to parameter of type 'DefaultValue | { [key: string]: { readonly __typename?: "HangingProtocol"; readonly smid: string; readonly name: string; readonly numberOfPriors: number; readonly created: Date; ... 4 more ...; readonly viewportsConfigurations?: readonly { ...; }[]; }; } | ((prevValue: { ...; }) => DefaultValue | { ...; })'.
      set(previewHangingProtocolState, (state) => ({
        ...state,
        [caseSmid]: hangingProtocol,
      }));
    },
});

// The HP the user has chosen for a case, stored as an identifier and nothing else. Persisted to
// localForage so the choice survives a refresh — but only after Confirm (or a create), never from
// previewing alone.
//
// Deliberately carries no payload. One atom holding a full HP meant "the user chose this" and "here
// is a copy of it, frozen at choice time" at the same time, and the frozen copy outranked freshly
// fetched server data in useHangingProtocol's priority chain. Clearing it to drop the stale copy
// also discarded the choice, which is what silently swapped the applied HP on Reset All (VX-4789)
// and after an edit-form Update (VX-4770). With the two split, dropping stale data and forgetting
// the choice are different operations that cannot be confused (VX-4809).
export const selectedHangingProtocolSmidState: RecoilState<{
  [key: string]: Nullable<string>;
}> = atom({
  key: 'selectedHangingProtocolSmidState',
  default: {},
  effects: [
    broadcastChannelSynchronizerEffect(),
    localForagePersisterEffect({
      version: 1,
    }),
  ],
});

export const selectedHangingProtocolSmidSelector: (
  arg1?: Nullable<string>
) => RecoilState<Nullable<string>> = selectorFamily<Nullable<string>, Nullable<string>>({
  key: 'selectedHangingProtocolSmidSelector',
  get:
    (caseSmid?: string | null) =>
    ({ get }) => {
      const state = get(selectedHangingProtocolSmidState);
      return state != null && caseSmid != null ? state[caseSmid] : null;
    },
  set:
    (caseSmid?: string | null) =>
    ({ set }, smid: Nullable<DefaultValue | string>) => {
      if (caseSmid == null) return;

      if (smid instanceof DefaultValue) {
        set(selectedHangingProtocolSmidState, (state) => omit([caseSmid], state));
        return;
      }

      set(selectedHangingProtocolSmidState, (state) => ({
        ...state,
        [caseSmid]: smid,
      }));
    },
});

// Case-resolved payload for the selected HP, cached per case. In-memory only (no persister): it is
// a cache, so a refresh re-resolves it from the selected smid rather than restoring a copy that may
// no longer match the server. Resetting an entry therefore means "this copy is stale", never "the
// user has no HP selected" — see useSelectedHangingProtocol.ts for the resolution rules.
//
// Known consequence, and a deliberate one: a refresh restores the user's *selection*, not the exact
// protocol they were looking at. If the HP was edited between the choice and the reload — by this
// user in another window, by someone else, or by a refetch whose result never reached this atom —
// the reload resolves the smid and renders the *current* version. So:
//
//   choose HP v1 -> HP becomes v2 on the server -> refresh -> the viewer shows v2, not v1.
//
// The old combined atom did persist the payload, which is what made a refresh reproduce the earlier
// render byte for byte. That is also exactly the defect this split removes: a persisted copy frozen
// at choice time outranked freshly fetched server data, and there was no way to drop it without also
// discarding the choice (VX-4789, VX-4770). Between "the render survives a reload" and "the applied
// protocol is never silently stale", VX-4809 picks the second. Anything that needs the first has to
// version the payload rather than cache it.
export const selectedHangingProtocolPayloadState: RecoilState<{
  [key: string]: Nullable<HangingProtocol>;
}> = atom({
  key: 'selectedHangingProtocolPayloadState',
  default: {},
  effects: [broadcastChannelSynchronizerEffect()],
});

export const selectedHangingProtocolPayloadSelector: (
  arg1?: Nullable<string>
) => RecoilState<Nullable<HangingProtocol>> = selectorFamily<
  Nullable<HangingProtocol>,
  Nullable<string>
>({
  key: 'selectedHangingProtocolPayloadSelector',
  get:
    (caseSmid?: string | null) =>
    ({ get }) => {
      const state = get(selectedHangingProtocolPayloadState);
      return state != null && caseSmid != null ? state[caseSmid] : null;
    },
  set:
    (caseSmid?: string | null) =>
    ({ set }, hangingProtocol: Nullable<DefaultValue | HangingProtocol>) => {
      if (caseSmid == null) return;

      if (hangingProtocol instanceof DefaultValue) {
        set(selectedHangingProtocolPayloadState, (state) => omit([caseSmid], state));
        return;
      }

      set(selectedHangingProtocolPayloadState, (state) => ({
        ...state,
        [caseSmid]: hangingProtocol,
      }));
    },
});

export const isPreviewingHangingProtocolState: RecoilState<boolean> = atom({
  key: 'isPreviewingHangingProtocolState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * The current step index, plus the (case, HP) pair it is positioned within.
 *
 * The attribution exists to answer one question no state used to record: **has anyone put the index
 * where it is for *this* protocol?** `useHangingProtocol` needs that to tell a genuine protocol
 * change, which should land on step 0, from a re-read of a protocol the reader is already navigating,
 * which must leave them alone (VX-4783).
 *
 * It has to ride on the index's own value rather than sit in a sibling atom or in module state, and
 * both of those alternatives were considered and rejected:
 *
 * - **Module state cannot answer it.** The second viewer window learns about a protocol created in
 *   the first one purely by broadcast and never runs an init pass for it, so the window that needs
 *   the answer is exactly the window with no local record. That is the measured VX-4810 trace where
 *   viewer/1 homed to 0 eight seconds after the reader had selected step 1 in viewer/0, and dragged
 *   viewer/0 with it because this atom is BroadcastChannel-synced.
 * - **A sibling broadcast atom cannot answer it safely.** Two independently-broadcast atoms have no
 *   ordering guarantee between them, so a window could observe the new index with the old owner. That
 *   is the failure family VX-4859 and VX-4862 already cost three tickets. One atom is one message.
 *
 * `ownerHPKey` is a {@link makeHPKey} value, so it carries the case as well as the HP smid: the same
 * HP smid can match different cases, and a navigation to a new case matching the same HP is a genuine
 * change that must still home (VX-4429).
 */
export type AttributedStepIndex = {
  index: number;
  /**
   * The smid of the step `index` pointed at when it was written, or null when the writer's list had
   * no step there (VX-4810 slice 2).
   *
   * The position and the identity are both recorded because they answer different questions, and the
   * position alone was the wrong one. An index means "the Nth step of whatever list you are holding",
   * so it only survives as long as every reader holds the same list -- and the two places that
   * assumption breaks are exactly the two places this atom is read. A reorder or a delete renumbers
   * the list under a reader who has not moved, and a BroadcastChannel message carries a number
   * computed against the *sending* window's list, which during an optimistic create or a deferred
   * re-init is provably not the receiving window's (see `pendingStepMutations.ts`).
   *
   * `stepSmid` makes the identity the thing that travels. {@link hangingProtocolStepIndex} re-resolves
   * it against the live steps on every read, so the reader follows their step through a renumbering
   * instead of being left pointing at whatever moved into its slot. The stored `index` remains the
   * fallback for the cases where identity cannot answer: before any step is published, in the HP form
   * once it has added steps the viewer does not have, and for a smid this window's list does not
   * contain.
   *
   * Steps carry a smid from creation onward -- the backend's on read, a `client-` one from
   * `createDefaultStep` before the mutation resolves -- so this is null in practice only for a step
   * that was never minted through either path.
   */
  stepSmid: string | null;
  /** The `hpKey` this index is positioned within, or null if nothing has positioned it. */
  ownerHPKey: string | null;
};

export const hangingProtocolStepIndexAttributed: RecoilState<AttributedStepIndex> =
  atom<AttributedStepIndex>({
    key: 'hpStepIndexAttributed',
    default: { index: 0, stepSmid: null, ownerHPKey: null },
    effects: [broadcastChannelSynchronizerEffect()],
  });

/**
 * Current sub-step index within a step (for prior scrolling).
 * Reset to 0 by callers when navigating to a new step (see `navigateToStep` in
 * `useHPStepOperations` and HP initialization in `useHangingProtocol`). There is no
 * automatic selector/effect wiring on the atom itself.
 */
export const hangingProtocolSubStepIndex: RecoilState<number> = atom({
  key: 'hpSubStepIndex',
  default: 0,
  effects: [broadcastChannelSynchronizerEffect()],
});

/**
 * Versioned wrapper for stepped HP configurations.
 * The version counter prevents stale BroadcastChannel echoes from overwriting
 * authoritative local updates (e.g., a clinical step reorder on viewer/0 being
 * overwritten by viewer/1's stale refetch data echoing back).
 */
export type VersionedStepConfigurations = {
  steps: StepConfiguration[] | null;
  /**
   * HP display name, kept here so activeHangingProtocolName can be a pure selector.
   *
   * `name` needs this duplication because useHangingProtocol's `__lastInitializedHPKey` guard
   * blocks re-initializing this atom on a same-case-and-smid update (protecting in-progress
   * local step edits), which would otherwise let a rename on one window go unseen by another
   * that didn't perform it -- see the explicit name-sync branch in useHangingProtocol.ts. If a
   * future field is added here for a similar reason, it needs that same manual sync, since the
   * guard will block it too (VX-4615 review).
   */
  name: string | null;
  version: number;
  /**
   * Which window minted this version, used only to break a tie between two windows that minted the
   * same version number. See {@link WRITER_ID} and {@link compareVersioned}.
   *
   * Optional because a value can legitimately carry no writer: the atom default, and a value
   * broadcast by a window still running a build from before this field existed. Both compare as the
   * lowest id, so a real write wins the tie against them. Every value this app mints has one --
   * `makeVersionedSteps` and `makeVersionedBaseline` are the only paths that produce these, which
   * `clinicalStepStore.guard.test.ts` holds in place.
   */
  writerId?: string;
};

/**
 * Identifies this window as a writer of the versioned step atoms, for the lifetime of the document.
 *
 * The version counter is minted as `prev.version + 1` off the writing window's own copy of a
 * cross-window-synced atom, so two windows writing from the same base mint the *same* number for
 * different content -- measured and documented in `pendingStepMutations.ts`. A merge that accepts
 * only a strictly higher version then has each window reject the other, and both keep their own
 * copy. This id is what lets the tie resolve to the same winner in both windows instead.
 *
 * It rides the atom's own value rather than a sibling atom deliberately: two independently
 * broadcast atoms have no ordering guarantee (the VX-4859/VX-4862 family), and one atom is one
 * message.
 */
const WRITER_ID = nanoid();

/**
 * Total order over two versioned values, so both windows pick the same winner (VX-4859).
 *
 * Version dominates, which preserves the VX-4862 guarantee that a stale republish cannot move a
 * newer window backwards. `writerId` only decides an exact tie, and it decides it by comparing the
 * two ids -- a rule that is symmetric, so the window holding A and the window holding B reach the
 * same verdict without exchanging anything further.
 *
 * Returns > 0 when `a` should win, < 0 when `b` should, and 0 only when they are the same write.
 */
const compareVersioned = (
  a: { version: number; writerId?: string },
  b: { version: number; writerId?: string }
): number => {
  // Coalesced because a value can reach here with no version at all -- a hand-built object, or a
  // payload from a build predating the field. Subtracting an absent version yields NaN, which
  // compares false against everything and would silently keep the local copy.
  const aVersion = a.version ?? 0;
  const bVersion = b.version ?? 0;
  if (aVersion !== bVersion) return aVersion - bVersion;
  // A value from an older build, or a default, carries no id. Treat it as the lowest, so a real
  // write always wins the tie rather than the comparison depending on undefined.
  const aWriter = a.writerId ?? '';
  const bWriter = b.writerId ?? '';
  if (aWriter === bWriter) return 0;
  // Compared by code unit rather than `localeCompare`: collation depends on the runtime's locale and
  // ICU data, so two windows under different locales could order the same pair of ids differently
  // and diverge again -- the exact failure this tie-break exists to remove. Code-unit order is the
  // same everywhere, which is all the rule needs to be (VX-4859 review).
  return aWriter < bWriter ? -1 : 1;
};

/**
 * BroadcastChannel merge function for steppedHPConfigurations.
 *
 * Highest version wins; an exact version tie is broken by writer id so the two windows converge
 * rather than each keeping its own copy (VX-4859). Exported for testing.
 */
export const steppedHPMergeFn = (
  oldValue: Nullable<VersionedStepConfigurations>,
  newValue: VersionedStepConfigurations
): VersionedStepConfigurations => {
  if (oldValue == null) return newValue;
  const accepted = compareVersioned(newValue, oldValue) > 0;
  // Same reason as {@link clinicalStepsBaselineMergeFn}: a broadcast arrival is a change no writer
  // in `clinicalStepStore` can report, so without this the steps atom also appears to move on its
  // own. The pair is traced together because a dirty dot is a function of both, and diagnosing one
  // against an unrecorded change in the other is what sent VX-5124 down two wrong paths.
  traceHPMutation('steppedHPMerge', {
    accepted,
    incomingSteps: newValue.steps?.length ?? null,
    localSteps: oldValue.steps?.length ?? null,
    incomingVersion: newValue.version,
    localVersion: oldValue.version,
    incomingWriter: newValue.writerId ?? null,
    localWriter: oldValue.writerId ?? null,
  });
  return accepted ? newValue : oldValue;
};

const DEFAULT_VERSIONED_STEPS: VersionedStepConfigurations = {
  steps: null,
  name: null,
  version: 0,
  writerId: '',
};

/**
 * Step configurations array from a stepped hanging protocol.
 * null when using legacy (flat) HP format.
 *
 * Wrapped in { steps, version } to prevent BroadcastChannel race conditions.
 */
export const steppedHPConfigurations: RecoilState<VersionedStepConfigurations> =
  atom<VersionedStepConfigurations>({
    key: 'steppedHPConfigurations',
    default: DEFAULT_VERSIONED_STEPS,
    effects: [broadcastChannelSynchronizerEffect({ mergeFn: steppedHPMergeFn })],
  });

/** Read just the steps array from the versioned atom value. */
export const getStepsFromVersioned = (
  versioned: VersionedStepConfigurations
): StepConfiguration[] | null => versioned.steps;

/**
 * Where `stepSmid` sits in `steps`, or null when the list does not contain it.
 *
 * Null rather than -1 so the caller has to handle "this list has no such step" explicitly; -1 read as
 * an index is a silent jump to the end under a negative-tolerant lookup, which is the class of bug
 * this whole slice exists to remove.
 *
 * First match wins, which is only well defined because step smids are unique by construction: the
 * backend mints one per step, and `createDefaultStep` overrides the smid on every clone, so even an
 * add-step that deep-copies its source cannot produce two steps with the same one.
 */
const findStepIndexBySmid = (
  steps: StepConfiguration[] | null,
  stepSmid: string | null
): number | null => {
  if (stepSmid == null || steps == null) return null;
  const index = steps.findIndex((step) => step.smid === stepSmid);
  return index === -1 ? null : index;
};

/**
 * The step index as a plain number — what every reader uses, and what tests and stories seed.
 *
 * A writable selector rather than a shape change at ~13 read sites and ~100 test sites, which is the
 * same move `d8c45a5ea6` used to give `clinicalStepsBaseline` a version without touching its readers.
 *
 * **Identity wins over position on read (VX-4810 slice 2).** The stored `stepSmid` is re-resolved
 * against the live steps every time, so a list that renumbers under a reader — a reorder, a delete,
 * or a BroadcastChannel message carrying a number computed against the sending window's list — moves
 * the reader *with* their step rather than leaving them on whatever slid into its slot. The stored
 * `index` is the fallback, used whenever identity cannot answer: no smid recorded yet, or a smid this
 * window's list does not contain.
 *
 * The resolution deliberately reads `steppedHPConfigurations`, the viewer's list, even while the HP
 * form is open on `hpFormStepConfigurations`. That is safe because a write resolves against the same
 * list a read does, so in-window the pair is a round trip that returns the number it was given. The
 * form's divergent steps — ones it has added that the viewer does not have — land past the end of the
 * viewer list, record no smid, and fall through to the stored index, which is exactly today's
 * behaviour. Giving the form its own index atom would be the cleaner shape, but it re-tunes the
 * form/viewer coupling that VX-4749 and VX-4783 already settled, so it is deliberately not this
 * change.
 *
 * A write here **preserves `ownerHPKey`**, and that is the load-bearing part rather than a
 * convenience: moving the index is navigation *within* a protocol, which is what a chip click, a
 * keyboard shortcut, an add-step and a form step switch all are. Only `clinicalStepStore`'s
 * `resetStepIndices` changes which protocol the index belongs to. Keeping it that way is what lets
 * every navigation site stay ignorant of the hpKey — and keeps the store free of any dependency on
 * the router or the current-case context, which a derive-it-per-write design would have required.
 */
export const hangingProtocolStepIndex: RecoilState<number> = selector<number>({
  key: 'hpStepIndex',
  get: ({ get }) => {
    const { index, stepSmid } = get(hangingProtocolStepIndexAttributed);
    const steps = getStepsFromVersioned(get(steppedHPConfigurations));
    return findStepIndexBySmid(steps, stepSmid) ?? index;
  },
  // Updater form rather than read-then-write, so the attribution is carried through by the spread
  // instead of by a separate `get`. That also keeps it on the right side of the Recoil trap this
  // domain has been bitten by: a `set` updater runs lazily, so state that must be fresh is read from
  // `prev` inside the updater rather than sampled before the write.
  //
  // `steps` is read outside the updater on purpose, and the same trap is why: a selector's `get` is
  // not available inside the lazily-run updater, and the steps list is not state this write has just
  // changed, so sampling it here is both necessary and correct.
  set: ({ set, get }, newValue) => {
    const index = newValue instanceof DefaultValue ? 0 : newValue;
    const stepSmid = getStepsFromVersioned(get(steppedHPConfigurations))?.[index]?.smid ?? null;
    set(hangingProtocolStepIndexAttributed, (prev) => ({ ...prev, index, stepSmid }));
  },
});

/** Derived from steppedHPConfigurations — resets automatically when the HP changes. */
export const activeHangingProtocolName = selector<string | null>({
  key: 'activeHangingProtocolName',
  get: ({ get }) => get(steppedHPConfigurations).name,
});

/**
 * Total number of sub-steps for the current step.
 * Derived from steppedHPConfigurations and hangingProtocolStepIndex so it stays
 * in sync without imperative setters.
 * 0 means no sub-steps (normal step without prior scrolling).
 */
export const hangingProtocolSubStepTotal = selector<number>({
  key: 'hpSubStepTotal',
  get: ({ get }) => {
    const stepIndex = get(hangingProtocolStepIndex);
    const step = getStepsFromVersioned(get(steppedHPConfigurations))?.[stepIndex] ?? null;
    return calculateSubStepTotal(step);
  },
});

/**
 * The hpKey whose init pass has reached a terminal decision -- `null` until then.
 *
 * Attributed to an hpKey rather than a bare boolean, because a boolean cannot distinguish "settled
 * for this protocol" from "settled for the one we just navigated away from". A boolean that only
 * ever flipped to `true` broke exactly that way: a new case's record has not hydrated yet, but the
 * flag was still `true` from the previous case; and a revisited case's record hydrates *instantly*
 * because `unsavedStepEditsHydration.ts` never forgets an hpKey it has already seen, so the flag read
 * settled while `steppedHPConfigurations` still held a different case's steps. Both misfiled writes
 * the third state exists to prevent, straight into a stale bucket.
 *
 * `useHangingProtocol` nulls this the instant `hpKey` changes, then re-stamps it with the new key
 * only once a pass reaches a decision under which `steppedHPConfigurations` is known correct for that
 * key -- see the decision-kind mapping at that call site, and `hpInitMachine.ts`'s `HPInitDecision`.
 *
 * Read only by {@link activeStepBucketSelector}, and written only by `useHangingProtocol`. Window-local
 * on purpose, same as before: it describes *this* window's progress through init, not a fact about
 * the case, so broadcasting it would let a settled window vouch for an unsettled one.
 */
export const steppedHPInitSettledFor: RecoilState<string | null> = atom<string | null>({
  key: 'hpInitSettledFor',
  default: null,
});

/**
 * Which bucket viewport overrides belong to right now: a step smid, {@link FLAT_BUCKET}, or `null`.
 *
 * `null` is the load-bearing third state. Before init settles, `steppedHPConfigurations.steps` is
 * `null` for a stepped protocol and a flat one alike, so filing a write as flat during that window
 * would lose it the moment the real steps land. Readers treat `null` as "no overrides"; writers defer.
 *
 * An in-range step whose `smid` is `null` -- an optimistically-created step still waiting on its
 * backend id -- has no bucket of its own yet, so its overrides land in flat until the server names
 * it. A later task in this plan renames temp-smid buckets to their real smid once that happens.
 */
/**
 * What the settled stamp should become when an instance of `useHangingProtocol` mounts or its hpKey
 * changes: unchanged if it already names this key, otherwise null.
 *
 * A rule rather than an inline updater because it is the fix for a blank-viewport regression and it
 * cannot be covered where it is used. Four instances of the hook share this atom, and nulling it
 * unconditionally on mount let a late instance un-settle the protocol it was joining -- one painted
 * frame with no bucket, which {@link activeStepBucketSelector} turns into "no overrides and no
 * protocol viewports". The gap is between `useLayoutEffect` and `useEffect`, either side of a paint,
 * and jsdom does not paint: a hook test flushes both inside one `act`, so the null is never
 * observable and a test written against the rendered value passes against the bug. Verified twice.
 * So the rule is pinned here instead, and the browser behaviour belongs to the live suite.
 */
export const nextStepInitSettledStamp = (previous: string | null, hpKey: string): string | null =>
  previous === hpKey ? previous : null;

export const activeStepBucketSelector: RecoilValueReadOnly<StepBucketKey | null> =
  selector<StepBucketKey | null>({
    key: 'activeStepBucket',
    get: ({ get }) => {
      if (get(steppedHPInitSettledFor) == null) return null;
      const steps = getStepsFromVersioned(get(steppedHPConfigurations));
      if (steps == null) return FLAT_BUCKET;
      return steps[get(hangingProtocolStepIndex)]?.smid ?? FLAT_BUCKET;
    },
  });

/**
 * Create a versioned steps value with an incremented version counter.
 * Use this for all writes to steppedHPConfigurations to ensure the version
 * counter advances, making the write authoritative over stale echoes.
 */
export const makeVersionedSteps = (
  steps: StepConfiguration[] | null,
  prev: VersionedStepConfigurations,
  name?: string | null
): VersionedStepConfigurations => ({
  steps,
  name: name !== undefined ? name : prev.name,
  version: prev.version + 1,
  writerId: WRITER_ID,
});

export type StepsUpdater =
  | StepConfiguration[]
  | null
  | ((prev: StepConfiguration[] | null) => StepConfiguration[] | null);

/**
 * Wraps a versioned atom setter so callers can work with plain StepConfiguration[] updaters.
 * Handles unwrapping prev.steps and re-wrapping with makeVersionedSteps.
 */
export function applyVersionedStepsUpdate(
  setVersioned: (
    updater: (prev: VersionedStepConfigurations) => VersionedStepConfigurations
  ) => void,
  updater: StepsUpdater
): void {
  setVersioned((prev) => {
    const newSteps = typeof updater === 'function' ? updater(prev.steps) : updater;
    return makeVersionedSteps(newSteps, prev);
  });
}

/**
 * Current step configuration derived from steppedHPConfigurations and hangingProtocolStepIndex.
 * Read-only selector — no imperative sync needed since source atoms already have BroadcastChannel.
 */
export const currentStepConfiguration = selector<StepConfiguration | null>({
  key: 'currentStepConfiguration',
  get: ({ get }) => {
    const versioned = get(steppedHPConfigurations);
    const stepIndex = get(hangingProtocolStepIndex);
    return getStepsFromVersioned(versioned)?.[stepIndex] ?? null;
  },
});

/**
 * Step configurations owned by the HP form during editing.
 * Isolated from the viewer's steppedHPConfigurations to prevent
 * race conditions when the matched HP reference changes.
 * null when the form is closed or steps are not enabled.
 */
export const hpFormStepConfigurations: RecoilState<StepConfiguration[] | null> = atom<
  StepConfiguration[] | null
>({
  key: 'hpFormStepConfigurations',
  default: null,
  effects: [],
});

/**
 * User preference to skip the "Discard changes?" confirmation modal.
 * Does not persist through page refreshes.
 */
export const skipDiscardConfirmationState: RecoilState<boolean> = atom({
  key: 'skipDiscardConfirmationState',
  default: false,
});

/**
 * Stores the initial form state when entering HP edit/create mode.
 * Used to detect if there are unsaved changes.
 */
export const initialHPFormStateSnapshot: RecoilState<unknown | null> = atom<unknown | null>({
  key: 'initialHPFormStateSnapshot',
  default: null,
});

/**
 * Baseline step configurations when entering HP edit/create mode.
 * Used to detect if there are unsaved changes to steps.
 */
export const hpFormStepsBaseline: RecoilState<StepConfiguration[] | null> = atom<
  StepConfiguration[] | null
>({
  key: 'initialStepConfigurationsSnapshot',
  default: null,
});

/** The baseline plus the counter that decides which of two windows' copies is newer. */
export type VersionedStepBaseline = {
  steps: StepConfiguration[] | null;
  version: number;
  /** Which window minted this version. See {@link WRITER_ID}. */
  writerId?: string;
};

const DEFAULT_VERSIONED_BASELINE: VersionedStepBaseline = {
  steps: null,
  version: 0,
  writerId: '',
};

/** Stamps a new baseline value with the next version. */
export const makeVersionedBaseline = (
  steps: StepConfiguration[] | null,
  prev: Nullable<VersionedStepBaseline>
): VersionedStepBaseline => ({
  steps,
  version: (prev?.version ?? 0) + 1,
  writerId: WRITER_ID,
});

/**
 * Highest version wins, exactly as {@link steppedHPMergeFn} does for the steps -- including the
 * writer-id tie-break, so the pair cannot disagree about which of two colliding writes won.
 *
 * This is what makes publishing the baseline safe. VX-4862 was an *unversioned* republish: a window
 * echoed a baseline it had been holding, that copy landed on top of a newer one, and the receiving
 * window's idea of what it had saved moved backwards (adding a step after Reset All produced two
 * steps when the steps had been added from the other window). A stale copy now carries a lower
 * version and is refused, so the race it caused cannot recur.
 */
/**
 * The step smids a baseline value knows about. Membership only -- content is not the question here.
 */
const baselineStepSmids = (value: VersionedStepBaseline): Set<string> =>
  new Set((value.steps ?? []).flatMap((s) => (s.smid != null ? [s.smid] : [])));

/**
 * {@link compareVersioned}, except that an **exact version tie** prefers the value that knows about
 * strictly more steps.
 *
 * The tie is not hypothetical and the plain writer-id tie-break gets it wrong. Measured on
 * `stepped-hp-priors-per-step` against a PR preview:
 *
 *   w0  accepted=true  incomingSteps=1 localSteps=2  incomingVersion=52 localVersion=52
 *   w1  accepted=false incomingSteps=2 localSteps=1  incomingVersion=52 localVersion=52
 *
 * Two windows minted version 52 off the same base for different content -- one baseline holding the
 * step just added, one predating it -- and `writerId` resolved it to the window that had never
 * heard of the step. Both windows converged, which is what that tie-break promises, but they
 * converged on the value that had *lost* a step. `useClinicalStepDirtyState` reads a step with no
 * baseline entry as clean, so the reader's unsaved edit silently lost the dirty dot and the
 * context-menu Save the dot gates. That is VX-5124's regression, and it is why two predicates on
 * *which viewports the adoption fills* both leaked: the loss happens in the merge, after any
 * writer has had its say.
 *
 * Symmetric, so it keeps the property the writer-id break exists for: both windows compare the same
 * two values and compute the same subset relation, so they still agree without exchanging anything
 * further. `writerId` still decides when neither side is a superset, which is the genuinely
 * divergent case where nothing about membership can adjudicate.
 *
 * **The trade, stated plainly.** A step *deletion* also produces a value with fewer steps, so on an
 * exact tie against a stale pre-delete value this now prefers the pre-delete one, where before it
 * would have picked arbitrarily. That is a narrower risk than it looks -- the previous behaviour was
 * already arbitrary on ties, so this replaces an unpredictable error with a directional one, and it
 * only applies to an exact collision rather than to deletions generally. It is deliberately not
 * extended to `steppedHPMergeFn`: the same collision is latent there and losing a step from the
 * steps atom would be worse, but nothing has measured it, and a delete resurrecting itself in the
 * *steps* atom is user-visible in a way a baseline entry is not (VX-4811 is that bug). Left as a
 * known asymmetry rather than a speculative second change.
 */
const compareBaselineVersioned = (a: VersionedStepBaseline, b: VersionedStepBaseline): number => {
  const byVersion = compareVersioned(a, b);
  if ((a.version ?? 0) !== (b.version ?? 0)) return byVersion;

  const aSmids = baselineStepSmids(a);
  const bSmids = baselineStepSmids(b);
  const aCoversB = [...bSmids].every((smid) => aSmids.has(smid));
  const bCoversA = [...aSmids].every((smid) => bSmids.has(smid));
  // A strict superset wins. Equal membership, or genuine divergence where neither covers the other,
  // falls through to the writer-id break.
  if (aCoversB && !bCoversA) return 1;
  if (bCoversA && !aCoversB) return -1;
  return byVersion;
};

export const clinicalStepsBaselineMergeFn = (
  oldValue: Nullable<VersionedStepBaseline>,
  newValue: VersionedStepBaseline
): VersionedStepBaseline => {
  if (oldValue == null) return newValue;
  const accepted = compareBaselineVersioned(newValue, oldValue) > 0;

  // Traced because a broadcast arrival is the one baseline change the store's writers cannot see.
  // `setSelf` from the synchronizer effect bypasses `writeBaseline` entirely, so a window whose
  // baseline moves underneath it shows a step count changing between two trace lines with no write
  // in between -- which is exactly how far the VX-5124 investigation could get without this. The
  // versions and writer ids are here because "version dominates" means the verdict is only
  // reconstructible if both sides' versions are on the record.
  traceHPMutation('clinicalStepsBaselineMerge', {
    accepted,
    incomingSteps: newValue.steps?.length ?? null,
    localSteps: oldValue.steps?.length ?? null,
    incomingVersion: newValue.version,
    localVersion: oldValue.version,
    incomingWriter: newValue.writerId ?? null,
    localWriter: oldValue.writerId ?? null,
  });

  return accepted ? newValue : oldValue;
};

/**
 * Backing store for {@link clinicalStepsBaseline}. Read and written through that selector, never
 * directly, so the version stamp cannot be skipped.
 */
const clinicalStepsBaselineAtom = atom<VersionedStepBaseline>({
  key: 'clinicalStepBaselineVersioned',
  default: DEFAULT_VERSIONED_BASELINE,
  effects: [broadcastChannelSynchronizerEffect({ mergeFn: clinicalStepsBaselineMergeFn })],
});

/**
 * Baseline of steppedHPConfigurations at HP load time (or last clinical save).
 * Used to detect per-step dirty state in clinical mode.
 *
 * **Broadcast, and it has to be.** `steppedHPConfigurations` crosses the window boundary, so an
 * operation that moves both atoms in one window moved only the *steps* in the other -- and a step
 * whose steps advanced while its baseline stood still reads dirty. That is a false unsaved-changes
 * dot on an edit nobody made, and with two viewer windows open on one case it fires constantly.
 *
 * It was briefly not broadcast (VX-4862), on the reasoning that the baseline is a derived fact every
 * window computes for itself from the same server response. That holds for a value each window
 * fetches independently; it does not hold for a local change that is then published, which is the
 * case the removal did not account for. Measured on the comprehensive live spec: 3 phases passing
 * without the effect against 16 with it.
 *
 * The original race is defended against by version rather than by silence -- see
 * {@link clinicalStepsBaselineMergeFn}.
 *
 * Kept as a writable selector over {@link clinicalStepsBaselineAtom} so every reader and every test
 * still sees a plain `StepConfiguration[] | null`; versioning is the store's business, not theirs.
 */
export const clinicalStepsBaseline: RecoilState<StepConfiguration[] | null> = selector<
  StepConfiguration[] | null
>({
  key: 'clinicalStepBaseline',
  get: ({ get }) => get(clinicalStepsBaselineAtom).steps,
  set: ({ set }, newValue) => {
    // A reset arrives as DefaultValue; it still needs a version, or the cleared baseline loses to
    // whatever the other window last published and the clear silently does not cross.
    const steps = newValue instanceof DefaultValue ? null : newValue;
    set(clinicalStepsBaselineAtom, (prev) => makeVersionedBaseline(steps, prev));
  },
});

/** How many case-and-HP pairs keep a persisted unsaved-edit record. Matches viewportsConfigurationsState. */
export const MAX_PERSISTED_UNSAVED_STEP_EDIT_KEYS = 250;

/**
 * The case-and-HP identity used by useHangingProtocol's init guard and by unsavedClinicalStepEdits.
 *
 * The same HP smid can match different cases, so keying on the smid alone would conflate them
 * (VX-4429). The sentinels keep a deleted or absent HP, and a missing case, producing a stable key.
 * Shared so the two call sites cannot drift apart -- a mismatch would silently orphan a persisted
 * record instead of clearing it.
 */
export function makeHPKey(caseId: Nullable<string>, hpSmid: Nullable<string>): string {
  return `${caseId ?? '__no_case__'}:${hpSmid ?? '__no_hp__'}`;
}

/**
 * A clinical step edit the user has made but not saved, plus the baseline it diverges from.
 *
 * Only dirty steps appear here, never the whole step array, so the record stays proportional to what
 * the user actually changed and its shape states what it means. `baselines` carries the baseline
 * entries for exactly the steps in `edits`, matched by smid; it is what lets the reload merge tell an
 * edited step from one the server simply reports differently.
 */
export interface UnsavedStepEditsRecord {
  edits: StepConfiguration[];
  baselines: StepConfiguration[];
}

/**
 * Unsaved clinical step edits, persisted so they survive a viewer refresh (VX-4803).
 *
 * Keyed by hpKey (`${caseId}:${hpSmid}`), the key useHangingProtocol already computes. That keying is
 * what makes this safe to persist at all: a reload only ever reads its own key, so one case's unsaved
 * edits cannot surface on another, and switching the applied HP produces a different key so the
 * departed HP's edits are simply not read (the VX-4624 guarantee).
 *
 * Deliberately no broadcastChannelSynchronizerEffect. Both viewer windows derive an identical record
 * from steppedHPConfigurations and clinicalStepsBaseline, which are already broadcast-synced, so the
 * windows converge without it. Because the family is keyed per hpKey, two windows on different cases
 * write different IndexedDB keys and cannot clobber each other -- the failure a single map-valued atom
 * would have had.
 *
 * The function form of `effects` is what lets onHydrated close over its own hpKey.
 *
 * BUMP `version` WHENEVER A STEP'S PERSISTED JSON SHAPE CHANGES. Retention here is an LRU cap on the
 * number of hpKeys, with no time limit, so a record can outlive the schema that produced it. The
 * backend revalidates `layouts` and `config_details` with pydantic on every single-step save, and
 * `HPStepConfigDetails` is `extra="forbid"` with strict enums (backend-api `ugc/models.py`), so a
 * record holding a key or enum value that has since been removed makes the save fail rather than
 * degrade. That is not hypothetical: backend commit 99b9c740f (2026-03-11) uppercased the
 * ChestWallAlignmentSetting, GrowthDirectionSetting and GrowthKey values with no data migration, which
 * would have invalidated any record written before it. Bumping the version makes
 * localForagePersisterEffect drop the previous version's key, which is the only thing standing between
 * a schema change and a user meeting an unexplainable validation error on Save.
 */
export const unsavedClinicalStepEdits: (
  hpKey: string
) => RecoilState<Nullable<UnsavedStepEditsRecord>> = atomFamily<
  Nullable<UnsavedStepEditsRecord>,
  string
>({
  key: 'unsavedClinicalStepEdits',
  default: null,
  effects: (hpKey) => [
    localForagePersisterEffect({
      version: 1,
      maxKeys: MAX_PERSISTED_UNSAVED_STEP_EDIT_KEYS,
      onHydrated: () => markUnsavedStepEditsHydrated(hpKey),
    }),
  ],
});

/**
 * Whether a clinical step creation is in flight.
 * Synced across windows so both viewer/0 and viewer/1 disable the
 * "Add Step" button while the mutation is pending.
 */
export const isCreatingStepState: RecoilState<boolean> = atom({
  key: 'isCreatingStepState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

// Shared with useInitStepBarPlacement.ts so it can check the same localStorage entry this atom's
// own persister effect writes to, without hand-duplicating the key/version and risking drift.
export const STEP_BAR_PLACEMENT_PERSISTER_VERSION = 1;

/**
 * User preference for step bar position relative to viewports.
 * Persisted to localStorage and synced across windows.
 */
export const stepBarPlacementState: RecoilState<StepBarPlacement> = atom<StepBarPlacement>({
  key: 'stepBarPlacement',
  default: StepBarPlacement.Bottom,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localStoragePersisterEffect({ version: STEP_BAR_PLACEMENT_PERSISTER_VERSION }),
  ],
});

// Shared for the same reason as the placement version above: a caller that needs to read or clear
// the entry this atom's persister writes should not hand-duplicate the key/version.
export const STEP_BAR_HEIGHT_PERSISTER_VERSION = 1;

/**
 * Persisted step bar height in pixels. Synced across viewer windows so that
 * resizing on viewer/0 is reflected on viewer/1. A value of 0 means the bar
 * is collapsed.
 */
export const stepBarHeightState: RecoilState<number> = atom<number>({
  key: 'stepBarHeight',
  default: STEP_BAR_DEFAULT_HEIGHT,
  effects: [
    broadcastChannelSynchronizerEffect(),
    localStoragePersisterEffect({ version: STEP_BAR_HEIGHT_PERSISTER_VERSION }),
  ],
});
